import {useEffect, useRef, useState} from 'react';
import {AlertCircle, CheckCircle2, Download, FileSpreadsheet, Loader2} from 'lucide-react';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Progress} from '@common-web/components/ui/progress';
import {RouterInput, trpc} from '@common-web/lib/trpc';
import {triggerMemberExportDownload} from '@client/lib/memberExportDownload';

type StartExportInput = RouterInput['vba']['members']['startExport'];

type MemberExportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Resolved tRPC input for the export — the same filter object the search
   * uses, minus pagination/sort. Pass `null` if you want to render the dialog
   * but haven't computed filters yet (e.g., still loading total count).
   */
  filters: StartExportInput | null;
};

/**
 * Wrapper around an async member CSV export. Owns:
 * - kicking off the job (`vba.members.startExport`) when opened
 * - polling status every 2s until `COMPLETED` or `FAILED`
 * - rendering a progress bar driven by `{processed, total}`
 * - auto-triggering a browser download on completion via the presigned URL
 *
 * The user can close the dialog at any time — the job keeps running, and the
 * `activeExport` banner on the parent page picks up the in-flight state.
 */
export function MemberExportDialog({open, onOpenChange, filters}: MemberExportDialogProps) {
  const [jobRunId, setJobRunId] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const downloadTriggeredRef = useRef(false);

  // Tracks the live `open` value so the `startExport` callbacks (which capture
  // the `open` from when the effect ran) can bail if the dialog has since
  // closed — otherwise a late-resolving mutation revives `jobRunId`/`total`
  // after the close-reset effect has cleared them, reviving the stale job on
  // the next open.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const utils = trpc.useUtils();
  const startExport = trpc.vba.members.startExport.useMutation();

  // Fire `startExport` once each time the dialog opens. The mutation result
  // gives us the `jobRunId` we then hand to the polling query below.
  useEffect(() => {
    if (!open || !filters) return;
    if (jobRunId || startExport.isPending) return;

    setStartError(null);
    downloadTriggeredRef.current = false;

    startExport.mutate(filters, {
      onSuccess: (data) => {
        if (!openRef.current) return;
        setJobRunId(data.jobRunId);
        setTotal(data.total);
        // Nudge the parent page's `activeExport` banner to pick up the
        // in-flight job immediately. Its polling self-disables once data is
        // null, so without this the banner stays hidden until a refresh when
        // the user runs the export in the background.
        void utils.vba.members.activeExport.invalidate();
      },
      onError: (err) => {
        if (!openRef.current) return;
        setStartError(err.message || 'Failed to start export');
      },
    });
  }, [open, filters, jobRunId, startExport, utils]);

  // Reset internal state when the dialog closes so a subsequent open kicks off
  // a fresh export instead of resuming the old one.
  useEffect(() => {
    if (open) return;
    setJobRunId(null);
    setTotal(null);
    setStartError(null);
    downloadTriggeredRef.current = false;
  }, [open]);

  const statusQuery = trpc.vba.members.exportStatus.useQuery(
    {jobRunId: jobRunId!},
    {
      enabled: !!jobRunId && open,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status === 'COMPLETED' || status === 'FAILED') return false;
        return 2000;
      },
    },
  );

  const status = statusQuery.data?.status;

  // Keep the parent page's `activeExport` banner in sync once the job reaches a
  // terminal state — the banner should disappear as soon as we know it's done,
  // not on the parent's next polling tick.
  useEffect(() => {
    if (status === 'COMPLETED' || status === 'FAILED') {
      void utils.vba.members.activeExport.invalidate();
    }
  }, [status, utils]);

  const progress = statusQuery.data?.progress ?? null;
  const downloadUrl = statusQuery.data?.downloadUrl ?? null;
  const rowCount = statusQuery.data?.rowCount ?? null;
  const fileName = statusQuery.data?.fileName ?? null;
  const failureMessage =
    startError ??
    (status === 'FAILED'
      ? (statusQuery.data?.errorMessage ?? 'Export failed unexpectedly')
      : statusQuery.isError
        ? (statusQuery.error.message || 'Lost connection to the export. Please try again.')
        : null);

  // Auto-trigger the browser download on the first COMPLETED poll. Routed
  // through the shared helper so a backgrounded completion (handled by
  // `useBackgroundMemberExport`) and the open dialog can't both download the
  // same job — whichever observes COMPLETED first wins, the other no-ops.
  useEffect(() => {
    if (status !== 'COMPLETED' || !downloadUrl || !jobRunId) return;
    if (downloadTriggeredRef.current) return;
    downloadTriggeredRef.current = true;
    triggerMemberExportDownload(jobRunId, downloadUrl, fileName);
  }, [status, downloadUrl, fileName, jobRunId]);

  const isStarting = startExport.isPending || (!!jobRunId && !status);
  const isRunning = status === 'PENDING' || status === 'RUNNING';
  const isComplete = status === 'COMPLETED';

  // Closing the dialog while the export is still running. We seed the parent
  // page's `activeExport` banner from the status we've already polled so it
  // shows immediately with live progress. Without this the banner can miss the
  // job entirely: its own poll self-disables whenever `activeExport` returns
  // null, which it does in the brief window before the worker creates the
  // `JobRun` row — and nothing re-triggers it once this dialog stops polling.
  const handleRunInBackground = () => {
    if (jobRunId && isRunning) {
      utils.vba.members.activeExport.setData(undefined, {
        jobRunId,
        fileName: fileName ?? 'Member export',
        status: status === 'PENDING' ? 'PENDING' : 'RUNNING',
        progress,
        total: total ?? progress?.total ?? null,
      });
    }
    onOpenChange(false);
  };
  const percent =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
      : isComplete
        ? 100
        : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <FileSpreadsheet className='h-5 w-5 text-brand-blue' aria-hidden />
            Export members to CSV
          </DialogTitle>
          <DialogDescription>
            {total !== null
              ? `Exporting ${total.toLocaleString()} matching member${total === 1 ? '' : 's'}. You can close this dialog — the export will continue in the background.`
              : 'Preparing your export…'}
          </DialogDescription>
        </DialogHeader>

        <div className='py-4 space-y-4'>
          {failureMessage ? (
            <div className='flex flex-col items-center gap-4 py-4'>
              <div className='rounded-full bg-red-100 p-3'>
                <AlertCircle className='h-6 w-6 text-red-600' />
              </div>
              <Alert variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription>{failureMessage}</AlertDescription>
              </Alert>
            </div>
          ) : isComplete ? (
            <div className='flex flex-col items-center gap-3 py-4 text-center'>
              <div className='rounded-full bg-emerald-100 p-3'>
                <CheckCircle2 className='h-6 w-6 text-emerald-600' />
              </div>
              <p className='text-sm font-medium text-brand-navy'>
                Export complete{rowCount !== null ? ` — ${rowCount.toLocaleString()} rows` : ''}
              </p>
              <p className='text-xs text-muted-foreground'>
                Your download should start automatically. If it didn’t, use the button below.
              </p>
            </div>
          ) : (
            <div className='space-y-3'>
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
                <span>
                  {isStarting
                    ? 'Queueing export…'
                    : progress
                      ? `Processing ${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()} members…`
                      : 'Waiting for the worker to pick this up…'}
                </span>
              </div>
              <Progress value={percent} className='h-2' />
              <p className='text-center text-xs tabular-nums text-muted-foreground'>
                {percent}% complete
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {isComplete && downloadUrl ? (
            <>
              <Button
                variant='outline'
                onClick={() => onOpenChange(false)}
                type='button'
              >
                Close
              </Button>
              <Button
                type='button'
                onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
              >
                <Download className='h-4 w-4 mr-2' />
                Download again
              </Button>
            </>
          ) : failureMessage ? (
            <Button variant='outline' onClick={() => onOpenChange(false)} type='button'>
              Close
            </Button>
          ) : (
            <Button
              variant='outline'
              onClick={handleRunInBackground}
              type='button'
              disabled={isRunning ? false : isStarting}
            >
              {isRunning ? 'Run in background' : 'Cancel'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
